import {ReportTaxonomy, ReportTaxonomyField, operatorLabel} from '../contracts/report-taxonomy.types';

/** The four operator families, and everything this module needs to know about each one.
 *
 * ONE table, deliberately. The families were previously encoded twice — four member Sets driving
 * `operatorForValues`, and a separate representatives list plus an excluding-set driving
 * `operatorsForField` — which meant a fifth family had to be added in two places and a miss failed
 * silently: the family would be converted between its single and multi forms but never offered, or
 * offered but never suppressed on a not-applicable field. Every function below derives from this.
 *
 * Order is the order a reader sees the choices in. `single` doubles as the family's representative,
 * because the picker offers MEANINGS rather than wire operators: IS and IN are one meaning whose
 * wire form depends only on how many values are ticked, and `operatorForValues` converts between
 * them on every change, so offering both would ask the reader to choose what the control decides. */
const OPERATOR_FAMILIES: {single: string; multi: string; excluding: boolean; requiresAll: boolean}[] = [
	{single: 'IS', multi: 'IN', excluding: false, requiresAll: false},
	{single: 'IS_NOT', multi: 'NOT_IN', excluding: true, requiresAll: false},
	{single: 'IS_ALL', multi: 'ALL_IN', excluding: false, requiresAll: true},
	{single: 'IS_NOT_ALL', multi: 'NOT_ALL_IN', excluding: true, requiresAll: true},
];

/** The family an operator belongs to, or undefined for one outside all four (a comparison
 * operator, or anything the schema permits that no family names). */
function familyOf(operator: string) {
	return OPERATOR_FAMILIES.find(family => family.single === operator || family.multi === operator);
}

export function defaultOperatorForDataType(): string {
	return 'IS';
}

export function operatorForValues(current: string, values: string[]): string {
	const family = familyOf(current);
	if (!family) {
		return current;
	}
	return values.length > 1 ? family.multi : family.single;
}

/** The operator choices to offer for one field, drawn from the field's own catalog entry.
 *
 * Never a client-side list. The catalog is where an operator's availability is decided, and it
 * differs per field on the same grain: a project's owner declares only IS/IN, because the upstream
 * predicate ignores the comparator and a negative would return the matching rows rather than
 * excluding them; a labels field declares all eight. Reading the catalog means narrowing a field's
 * declared operators upstream narrows this control with no change here.
 *
 * Comparison operators (LESS, GREATER, ...) are absent by construction: no family names them, and
 * the fields that declare them are dates and numbers, none of which reach this control on this grain. */
export function operatorsForField(taxonomy: ReportTaxonomy, field: ReportTaxonomyField): {value: string; label: string}[] {
	// A boolean field offers no operator choice, because its VALUE list already expresses both
	// directions: the control holds exactly one of Yes and No, so `Is not Yes` and `Is No` select
	// the same rows. Offering the family here would put two controls on the tile that mean the same
	// thing, let the reader reach one answer two ways, and summarise one of them as "Is not Yes".
	if (field.dataType === 'boolean') {
		return [];
	}

	const declared = new Set(field.operators ?? []);
	// A field the catalog marks not-applicable for some rows cannot be safely EXCLUDED on, only
	// included on. The engine compares a row's value and treats an absent one as no-match, so a
	// negation drops every not-applicable row along with the ones the reader meant to exclude -
	// silently, and for fields like retainer over/under that is most of the portfolio. Until a
	// negation can state what it does with an absent value, the exclude families are not offered
	// on these fields; include still behaves exactly as the reader expects.
	const excludeSuppressed = field.notApplicableRule != null;

	return OPERATOR_FAMILIES.filter(family => {
		if (excludeSuppressed && family.excluding) {
			return false;
		}
		return declared.has(family.single) || declared.has(family.multi);
	}).map(family => ({
		value: family.single,
		label: operatorLabel(taxonomy, family.single),
	}));
}

/** Whether an operator requires EVERY selected value to match rather than any one of them.
 *
 * A summary joins values with a conjunction, and which word is correct is decided here: the
 * all-families read "and", every other family reads "or". Naming the wire operators rather than a
 * family representative means a stored multi-value member (ALL_IN, NOT_ALL_IN) is recognised too,
 * which is the form a saved condition actually carries. */
export function requiresEveryValue(operator: string): boolean {
	return familyOf(operator)?.requiresAll ?? false;
}
